import React, { useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { SignUpData } from '../SignUp';
import { formatPhoneNumber } from '@/common/utils/textFormat';

/**
 * 2단계: 본인인증 컴포넌트
 */

interface VerificationStepProps {
  data: SignUpData;
  onNext: (data: Partial<SignUpData>) => void;
  onPrev: () => void;
}

const VerificationStep: React.FC<VerificationStepProps> = ({ data, onNext, onPrev }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isDuplicateAccount, setIsDuplicateAccount] = useState(false);
  const [verificationData, setVerificationData] = useState({
    name: data.name,
    phone: data.phone,
    birthGender: data.birthGender,
    userDi: data.userDi,
    userCi: data.userCi,
    token: data.token,
  });

  /**
   * 본인인증 성공 핸들러
   */
  const handleVerificationSuccess = (certifyData: InisisAuthResult) => {
    if (!certifyData || !certifyData.userDi || certifyData.userDi.length === 0) {
      alert('인증에 실패했습니다. 다시 시도해주세요.');
      return;
    }

    const name = certifyData.userName || '';
    const phone = formatPhoneNumber(certifyData.userPhone || '');
    /**
     * certifyData.userBirth: "19900812" (YYYYMMDD)
     * certifyData.userGender: "M" | "F"
     * 포맷: "900812-1" (앞 2자리 + MMDD + - + 성별코드)
     */
    let birthGender = '';
    if (certifyData.userBirth && certifyData.userGender) {
      const year = certifyData.userBirth.slice(0, 4);
      const shortBirth = certifyData.userBirth.slice(2); // YYMMDD
      let genderCode = '';
      if (parseInt(year, 10) >= 2000) {
        genderCode = certifyData.userGender === 'M' ? '3' : '4';
      } else {
        genderCode = certifyData.userGender === 'M' ? '1' : '2';
      }
      birthGender = `${shortBirth}-${genderCode}******`;
    }

    // 인증 데이터에서 정보 추출 (실제 구현에서는 API 응답에 따라 조정)
    const extractedData = {
      name: name,
      phone: phone,
      birthGender: birthGender,
      userDi: certifyData.userDi,
      userCi: certifyData.userCi,
      token: certifyData.token,
    };

    setVerificationData(extractedData);
    
    // 중복 계정 체크
    if (certifyData.isDuplicate) {
      setIsDuplicateAccount(true);
    } else {
      setIsVerified(true);
    }
  };

  /**
   * 로그인 페이지로 이동
   */
  const handleGoToLogin = () => {
    window.location.href = '/login';
  };

  /**
   * 회원가입 계속 진행
   */
  const handleContinueSignUp = () => {
    setIsDuplicateAccount(false);
    setIsVerified(true);
  };

  /**
   * 다시 인증하기
   */
  const handleRetryAuth = () => {
    setIsDuplicateAccount(false);
    setIsVerified(false);
    setVerificationData({
      name: data.name,
      phone: data.phone,
      birthGender: data.birthGender,
      userDi: data.userDi,
      userCi: data.userCi,
      token: data.token,
    });
  };

  /**
   * 다음 단계로 진행
   */
  const handleNext = () => {
    if (!isVerified) {
      alert('본인인증을 완료해주세요.');
      return;
    }
    onNext(verificationData);
  };

  return (
    <Container>
      <VerificationContainer>
        {isDuplicateAccount ? (
          <DuplicateAccountPrompt>
            <DuplicateIcon>⚠️</DuplicateIcon>
            <DuplicateTitle>이미 가입된 계정이 있습니다</DuplicateTitle>
            <DuplicateDescription>
              인증하신 정보로 이미 가입된 계정이 존재합니다.<br />
              기존 계정으로 로그인하시거나, 다른 정보로 회원가입을 진행해주세요.
            </DuplicateDescription>
            
            <DuplicateUserInfo>
              <InfoItem>
                <InfoLabel>이름</InfoLabel>
                <InfoValue>{verificationData.name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>휴대폰번호</InfoLabel>
                <InfoValue>{verificationData.phone}</InfoValue>
              </InfoItem>
            </DuplicateUserInfo>
            
            <DuplicateActionButtons>
              <Button
                $variant="gradient"
                $size="md"
                onClick={handleGoToLogin}
              >
                기존 계정으로 로그인
              </Button>
              <Button
                $variant="outline"
                $size="md"
                onClick={handleRetryAuth}
              >
                다시 인증하기
              </Button>
            </DuplicateActionButtons>
          </DuplicateAccountPrompt>
        ) : !isVerified ? (
          <VerificationPrompt>
            <PromptIcon>🔐</PromptIcon>
            <PromptTitle>본인인증이 필요합니다</PromptTitle>
            <PromptDescription>
              휴대폰 인증을 통해 본인확인을 진행해주세요.<br />
              인증 완료 후 회원가입을 계속할 수 있습니다.
            </PromptDescription>
            
            <AuthButtonWrapper>
              <InisisAuthButton
                $variant="gradient"
                $size="md"
                $title="본인인증"
                onSuccess={handleVerificationSuccess}
              />
            </AuthButtonWrapper>
          </VerificationPrompt>
        ) : (
          <VerificationResult>
            <ResultIcon>✅</ResultIcon>
            <ResultTitle>본인인증이 완료되었습니다</ResultTitle>
            
            <VerifiedInfo>
              <InfoItem>
                <InfoLabel>이름</InfoLabel>
                <InfoValue>{verificationData.name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>휴대폰번호</InfoLabel>
                <InfoValue>{verificationData.phone}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>생년월일-성별</InfoLabel>
                <InfoValue>{verificationData.birthGender}</InfoValue>
              </InfoItem>
            </VerifiedInfo>
          </VerificationResult>
        )}
      </VerificationContainer>

      {!isDuplicateAccount && (
        <ButtonContainer>
          <Button
            $variant="outline"
            $size="md"
            onClick={onPrev}
          >
            이전
          </Button>
          <Button
            $variant="gradient"
            $size="md"
            disabled={!isVerified}
            onClick={handleNext}
          >
            다음
          </Button>
        </ButtonContainer>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const VerificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  background-color: ${semanticColors.background.secondary};
  min-height: 300px;
  justify-content: center;
`;

const VerificationPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
`;

const PromptIcon = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
`;

const PromptTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const PromptDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const AuthButtonWrapper = styled.div`
  margin-top: 16px;
`;

const VerificationResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  width: 100%;
`;

const ResultIcon = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
`;

const ResultTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const VerifiedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 280px;
  margin-top: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: ${semanticColors.background.primary};
  border-radius: 6px;
`;

const InfoLabel = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
`;

const InfoValue = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

// 중복 계정 관련 스타일
const DuplicateAccountPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  width: 100%;
`;

const DuplicateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
`;

const DuplicateTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.background.warning.primary};
  margin: 0;
`;

const DuplicateDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const DuplicateUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 280px;
  margin: 16px 0;
  padding: 16px;
  background-color: ${semanticColors.background.warning.secondary};
  border: 1px solid ${semanticColors.background.warning.primary};
  border-radius: 8px;
`;

const DuplicateActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 280px;
  margin-top: 16px;
  
  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: center;
  }
`;

export default VerificationStep;
